import React, { useEffect, useState } from 'react';
import {
  LineChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
} from 'recharts';
import { getLoginActivityReport, LoginActivityReport } from '../services/api';
import { useSidebar } from '../contexts/SidebarContext';
import { useNewGui } from '../hooks/useNewGui';
import PageHeader from '../components/shared/PageHeader';

type ChartView = 'line' | 'bar' | 'table';

/** Tooltip payload item from recharts (payload contains the raw data point) */
type TooltipPayloadItem = { payload?: { date?: string }; value?: number; name?: string };

/** Design-token hex values (tailwind.config.js) for recharts — Tailwind classes don't apply to SVG strokes. */
const TOKEN = {
  accent: '#0B5FD0',
  warning: '#9A6700',
  success: '#1A7F4B',
  danger: '#C0392B',
  info: '#0B6E8A',
  borderSubtle: '#D8DDE4',
  inkSecondary: '#5A6474',
} as const;

const CLASSIC_SERIES = {
  usersGenerated: '#3b82f6',
  loginsDisabled: '#f59e0b',
  loginsEnabled: '#22c55e',
  accountExpired: '#ef4444',
} as const;

const TOKEN_SERIES = {
  usersGenerated: TOKEN.accent,
  loginsDisabled: TOKEN.warning,
  loginsEnabled: TOKEN.success,
  accountExpired: TOKEN.danger,
} as const;

const LoginActivityReportPage: React.FC = () => {
  const { isSidebarOpen } = useSidebar();
  const useNew = useNewGui();
  const [report, setReport] = useState<LoginActivityReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chartView, setChartView] = useState<ChartView>('line');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getLoginActivityReport();
        if (!cancelled) setReport(data);
      } catch (e) {
        if (!cancelled) setError((e as Error).message || 'Failed to load report');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const shellClass = `flex-1 p-6 transition-all duration-300 ${
    useNew ? 'bg-surface-app font-plex' : 'bg-white'
  } ${isSidebarOpen ? 'ml-64' : 'ml-20'}`;

  if (loading) {
    return (
      <div className={shellClass}>
        <div className="flex items-center justify-center py-12">
          <div className={`animate-spin rounded-full h-12 w-12 border-b-4 ${useNew ? 'border-accent' : 'border-blue-600'}`} />
          <span className={`ml-4 text-lg ${useNew ? 'text-ink-secondary' : 'text-gray-600'}`}>
            Loading login activity report...
          </span>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className={shellClass}>
        {useNew ? (
          <PageHeader title="Login Activity Report" purpose="Weekly summary and 30-day trend for logins and account expiry." />
        ) : (
          <h2 className="text-3xl font-bold mb-4">Login Activity Report</h2>
        )}
        <div className={useNew
          ? 'rounded-lg bg-danger/10 border border-danger/30 text-danger px-4 py-3 text-sm'
          : 'rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3'
        }>
          {error || 'No data available'}
        </div>
      </div>
    );
  }

  const { weekly, trend } = report;
  const maxTrend = {
    usersGenerated: Math.max(1, ...trend.map((d) => d.usersGenerated)),
    loginsDisabled: Math.max(1, ...trend.map((d) => d.loginsDisabled)),
    loginsEnabled: Math.max(1, ...trend.map((d) => d.loginsEnabled)),
    accountExpired: Math.max(1, ...trend.map((d) => d.accountExpired)),
  };

  const series = useNew ? TOKEN_SERIES : CLASSIC_SERIES;

  const cards = useNew
    ? [
        { label: 'Logins created (last 7 days)', value: weekly.loginsCreated, bar: 'bg-accent', card: 'border-accent/20 bg-accent/5' },
        { label: 'Logins disabled (last 7 days)', value: weekly.loginsDisabled, bar: 'bg-warning', card: 'border-warning/20 bg-warning/5' },
        { label: 'Logins enabled (last 7 days)', value: weekly.loginsEnabled, bar: 'bg-success', card: 'border-success/20 bg-success/5' },
        { label: 'Total users with expired account', value: weekly.totalAccountExpired, bar: 'bg-danger', card: 'border-danger/20 bg-danger/5' },
        { label: 'Users whose account expired last week', value: weekly.expiredLastWeek, bar: 'bg-warning', card: 'border-warning/20 bg-warning/5' },
        { label: 'Account expiry extended (last 7 days)', value: weekly.expiryExtended, bar: 'bg-info', card: 'border-info/20 bg-info/5' },
      ]
    : [
        { label: 'Logins created (last 7 days)', value: weekly.loginsCreated, bar: 'bg-blue-500', card: 'border-gray-200 bg-gray-50' },
        { label: 'Logins disabled (last 7 days)', value: weekly.loginsDisabled, bar: 'bg-amber-500', card: 'border-gray-200 bg-gray-50' },
        { label: 'Logins enabled (last 7 days)', value: weekly.loginsEnabled, bar: 'bg-green-500', card: 'border-gray-200 bg-gray-50' },
        { label: 'Total users with expired account', value: weekly.totalAccountExpired, bar: 'bg-red-500', card: 'border-gray-200 bg-gray-50' },
        { label: 'Users whose account expired last week', value: weekly.expiredLastWeek, bar: 'bg-orange-500', card: 'border-gray-200 bg-gray-50' },
        { label: 'Account expiry extended (last 7 days)', value: weekly.expiryExtended, bar: 'bg-teal-500', card: 'border-gray-200 bg-gray-50' },
      ];

  const toggleBtn = (active: boolean, withBorder = false) => {
    if (useNew) {
      return `px-4 py-2 text-sm font-medium ${withBorder ? 'border-l border-border-subtle' : ''} ${
        active ? 'bg-accent text-white' : 'bg-surface text-ink-secondary hover:bg-surface-raised'
      }`;
    }
    return `px-4 py-2 text-sm font-medium ${withBorder ? 'border-l border-gray-300' : ''} ${
      active ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
    }`;
  };

  const chartPanelClass = useNew
    ? 'rounded-lg border-2 border-border-strong bg-surface p-4 mb-6'
    : 'rounded-lg border border-gray-200 bg-white p-4 mb-6';
  const gridStroke = useNew ? TOKEN.borderSubtle : '#e5e7eb';

  return (
    <div className={shellClass}>
      {useNew ? (
        <PageHeader title="Login Activity Report" purpose="Weekly summary and 30-day trend for logins and account expiry." />
      ) : (
        <>
          <h2 className="text-3xl font-bold mb-2">Login Activity Report</h2>
          <p className="text-gray-600 mb-6">Weekly summary and 30-day trend for logins and account expiry.</p>
        </>
      )}

      {/* Weekly report cards */}
      <section className="mb-8">
        <h3 className={useNew ? 'text-base font-semibold text-ink-primary mb-4' : 'text-xl font-semibold mb-4'}>
          Weekly report (last 7 days)
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {cards.map((card, i) => (
            <div
              key={i}
              className={`rounded-lg border p-4 shadow-sm ${card.card}`}
            >
              <div className={`h-1 w-12 rounded ${card.bar} mb-2`} />
              <div className={`text-sm font-medium ${useNew ? 'text-ink-secondary' : 'text-gray-600'}`}>{card.label}</div>
              <div className={`text-2xl font-bold mt-1 ${useNew ? 'text-ink-primary' : 'text-gray-900'}`}>{card.value}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Trend (last 30 days) - Chart and Table */}
      <section>
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <h3 className={useNew ? 'text-base font-semibold text-ink-primary' : 'text-xl font-semibold'}>
            Trend (last 30 days)
          </h3>
          <div className={`flex rounded-lg overflow-hidden border ${useNew ? 'border-border-strong' : 'border-gray-300'}`}>
            <button type="button" onClick={() => setChartView('line')} className={toggleBtn(chartView === 'line')}>
              Line chart
            </button>
            <button type="button" onClick={() => setChartView('bar')} className={toggleBtn(chartView === 'bar', true)}>
              Bar chart
            </button>
            <button type="button" onClick={() => setChartView('table')} className={toggleBtn(chartView === 'table', true)}>
              Table
            </button>
          </div>
        </div>

        {chartView !== 'table' && (() => {
          const chartData = trend.map((d) => ({
            ...d,
            shortDate: new Date(d.date + 'Z').toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
          }));
          const chartHeight = 360;

          if (chartView === 'line') {
            return (
              <div className={chartPanelClass} style={{ minHeight: chartHeight }}>
                <ResponsiveContainer width="100%" height={chartHeight}>
                  <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                    <XAxis dataKey="shortDate" tick={{ fontSize: 11, fill: useNew ? TOKEN.inkSecondary : undefined }} />
                    <YAxis tick={{ fontSize: 11, fill: useNew ? TOKEN.inkSecondary : undefined }} allowDecimals={false} />
                    <Tooltip
                      labelFormatter={(_label: string, payload?: TooltipPayloadItem[]) => payload?.[0]?.payload?.date ?? _label}
                      formatter={(value: number) => [value, '']}
                      contentStyle={{ fontSize: 12 }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line type="monotone" dataKey="usersGenerated" name="Users generated" stroke={series.usersGenerated} strokeWidth={2} dot={{ r: 2 }} />
                    <Line type="monotone" dataKey="loginsDisabled" name="Logins disabled" stroke={series.loginsDisabled} strokeWidth={2} dot={{ r: 2 }} />
                    <Line type="monotone" dataKey="loginsEnabled" name="Logins enabled" stroke={series.loginsEnabled} strokeWidth={2} dot={{ r: 2 }} />
                    <Line type="monotone" dataKey="accountExpired" name="Account expiry count" stroke={series.accountExpired} strokeWidth={2} dot={{ r: 2 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            );
          }

          return (
            <div className={chartPanelClass} style={{ minHeight: chartHeight }}>
              <ResponsiveContainer width="100%" height={chartHeight}>
                <ComposedChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                  <XAxis dataKey="shortDate" tick={{ fontSize: 11, fill: useNew ? TOKEN.inkSecondary : undefined }} />
                  <YAxis tick={{ fontSize: 11, fill: useNew ? TOKEN.inkSecondary : undefined }} allowDecimals={false} />
                  <Tooltip
                    labelFormatter={(_label: string, payload?: TooltipPayloadItem[]) => payload?.[0]?.payload?.date ?? _label}
                    formatter={(value: number) => [value, '']}
                    contentStyle={{ fontSize: 12 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="usersGenerated" name="Users generated" fill={series.usersGenerated} radius={[2, 2, 0, 0]} />
                  <Bar dataKey="loginsDisabled" name="Logins disabled" fill={series.loginsDisabled} radius={[2, 2, 0, 0]} />
                  <Bar dataKey="loginsEnabled" name="Logins enabled" fill={series.loginsEnabled} radius={[2, 2, 0, 0]} />
                  <Bar dataKey="accountExpired" name="Account expiry count" fill={series.accountExpired} radius={[2, 2, 0, 0]} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          );
        })()}

        {chartView === 'table' && (
          <div className={`overflow-x-auto rounded-lg border ${useNew ? 'border-2 border-border-strong' : 'border-gray-200'}`}>
            <table className="min-w-full text-sm">
              <thead className={useNew ? 'bg-surface-raised text-left' : 'bg-gray-100 text-left'}>
                <tr>
                  {['Date', 'Users generated', 'Logins disabled', 'Logins enabled', 'Account expiry count'].map((h) => (
                    <th
                      key={h}
                      className={`py-2 px-3 font-semibold ${useNew ? 'text-ink-primary border-b border-border-subtle' : 'text-gray-700'}`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {trend.map((day, i) => (
                  <tr
                    key={day.date}
                    className={useNew
                      ? (i % 2 === 0 ? 'bg-surface' : 'bg-surface-raised')
                      : (i % 2 === 0 ? 'bg-white' : 'bg-gray-50')}
                  >
                    <td className={`py-2 px-3 whitespace-nowrap ${useNew ? 'text-ink-primary' : 'text-gray-800'}`}>{day.date}</td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-2">
                        <div
                          className={`h-5 rounded min-w-[2px] ${useNew ? 'bg-accent' : 'bg-blue-500'}`}
                          style={{ width: `${(day.usersGenerated / maxTrend.usersGenerated) * 100}%`, maxWidth: '120px' }}
                        />
                        <span className={useNew ? 'text-ink-primary' : undefined}>{day.usersGenerated}</span>
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-2">
                        <div
                          className={`h-5 rounded min-w-[2px] ${useNew ? 'bg-warning' : 'bg-amber-500'}`}
                          style={{ width: `${(day.loginsDisabled / maxTrend.loginsDisabled) * 100}%`, maxWidth: '120px' }}
                        />
                        <span className={useNew ? 'text-ink-primary' : undefined}>{day.loginsDisabled}</span>
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-2">
                        <div
                          className={`h-5 rounded min-w-[2px] ${useNew ? 'bg-success' : 'bg-green-500'}`}
                          style={{ width: `${(day.loginsEnabled / maxTrend.loginsEnabled) * 100}%`, maxWidth: '120px' }}
                        />
                        <span className={useNew ? 'text-ink-primary' : undefined}>{day.loginsEnabled}</span>
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-2">
                        <div
                          className={`h-5 rounded min-w-[2px] ${useNew ? 'bg-danger' : 'bg-red-500'}`}
                          style={{ width: `${(day.accountExpired / maxTrend.accountExpired) * 100}%`, maxWidth: '120px' }}
                        />
                        <span className={useNew ? 'text-ink-primary' : undefined}>{day.accountExpired}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default LoginActivityReportPage;
